/**
 *  Example usage:
 *  <video-player class="muted group block">
      {{ section.settings.video | video_tag: preload: "none", muted: true, autoplay: false, loop: true, playsinline: true, controls: false, image_size: '1440x', class: video_class }}
      <div class="mt-4 flex items-center gap-x-4">
        <button type="button" data-video="play-pause">
          {%- render 'global-icon', icon: 'play', icon_class: 'w-6 group-[.playing]:hidden' -%}
          {%- render 'global-icon', icon: 'pause', icon_class: 'w-6 hidden group-[.playing]:block' -%}
        </button>
        <button type="button" data-video="mute">
          {%- render 'global-icon', icon: 'sound', icon_class: 'w-6 group-[.muted]:hidden' -%}
          {%- render 'global-icon', icon: 'muted', icon_class: 'w-6 hidden group-[.muted]:block' -%}
        </button>
        <div class="grow">
          <label for="progress" class="sr-only">Progress</label>
          <input
            type="range"
            name="progress"
            id="progress"
            min="0"
            max="{{ section.settings.video.duration }}"
            value="0"
            class="field__range"
            data-video="progress"
            style="--percentage: 0;"
          />
        </div>
      </div>
    </video-player>
 */

if (!customElements.get('video-player')) {
  /**
   * class VideoPlayer
   * @extends HTMLElement
   * @property {HTMLVideoElement | null} video
   */
  class VideoPlayer extends HTMLElement {
    /** @type {HTMLVideoElement | null} */
    video = null;

    connectedCallback() {
      const video = this.querySelector('video');
      if (video) {
        this.video = video;
        this.initialize();
      }
    }

    disconnectedCallback() {}

    initialize() {
      // Select elements in the dom that we need for the video player
      this.playPause = this.querySelector('[data-video="play-pause"]');
      this.mute = this.querySelector('[data-video="mute"]');
      this.progressInput = this.querySelector('[data-video="progress"]');

      // Add event listeners to elements
      this.playPause.addEventListener('click', () => {
        this.togglePlayPause();
      });

      this.video.addEventListener('click', () => {
        this.togglePlayPause();
      });

      this.mute.addEventListener('click', () => {
        this.toggleMute();
      });

      this.progressInput.addEventListener('input', (event) => {
        this.handleProgressInput(event);
      });

      this.progressInput.addEventListener('mousedown', (event) => {
        this.pauseVideo();
      });

      this.progressInput.addEventListener('mouseup', (event) => {
        this.playVideo();
      });

      this.video.addEventListener('timeupdate', (event) => {
        // do nothing when the video is seeking
        if (this.video.seeking) return;
        // Update custom input value, and set progress percentage on input for background gradient
        this.progressInput.value = this.video.currentTime * 1000;
        this.setProgressInputPercentage();
      });
    }

    /**
     * togglePlayPause
     * @description Set the video to play or pause.
     */
    togglePlayPause() {
      if (this.video.paused) {
        this.playVideo();
      } else {
        this.pauseVideo();
      }
    }

    /**
     * toggleMute
     * @description Set the video to mute or unmute.
     */
    toggleMute() {
      this.video.muted = !this.video.muted;
      if (this.video.muted) {
        this.unmuteVideo();
      } else {
        this.muteVideo();
      }
    }

    /**
     * unmuteVideo
     */
    unmuteVideo() {
      this.video.removeAttribute('muted');
      this.classList.add('muted');
    }

    /**
     * muteVideo
     */
    muteVideo() {
      this.video.setAttribute('muted', true);
      this.classList.remove('muted');
    }

    /**
     * handleProgressInput
     * @param {Object} event
     */
    handleProgressInput(event) {
      // Read the change input value
      this.video.currentTime = event.currentTarget.value / 1000;

      // set visual progress with background
      this.setProgressInputPercentage();
    }

    /**
     * setProgressInputPercentage
     * @returns {number} percentage
     */
    setProgressInputPercentage() {
      const max = this.progressInput.getAttribute('max');
      const percentage = 100 * (this.progressInput.value / max);
      this.progressInput.style.setProperty('--percentage', `${percentage}%`);
      return percentage;
    }

    /**
     * pauseVideo
     */
    pauseVideo() {
      this.video.pause();
      this.classList.remove('playing');
    }

    /**
     * playVideo
     */
    playVideo() {
      this.video.play();
      this.classList.add('playing');
    }
  }

  window.VideoPlayer = VideoPlayer;
  customElements.define('video-player', VideoPlayer);
}
